/**
 * PredictionChart Component
 *
 * Tesla-style visualisation showing prediction accuracy:
 * - X-axis: Weeks
 * - Y-axis: Kilograms
 * - Lines:
 *   - Original prediction (dashed grey)
 *   - Calibrated prediction (solid blue)
 *   - Actuals (green dots/line)
 * - Shaded area: Confidence band (2 sigma bounds)
 * - "Now" marker vertical line for current week
 *
 * US-FORECAST-CALIBRATION Step 8
 */

import { useMemo } from 'react'
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/**
 * Format week date for X-axis display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date (e.g., "6 Jan")
 */
function formatWeekLabel(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  })
}

/**
 * Custom tooltip component
 */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null

  // Get all values from payload
  const data = payload[0]?.payload || {}

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-slate-900 mb-2">
        Week of {formatWeekLabel(data.prediction_week)}
      </p>

      <div className="space-y-1">
        {data.raw_predicted_kg != null && (
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Original:</span>
            <span className="font-medium text-slate-400">
              {data.raw_predicted_kg.toFixed(1)} kg
            </span>
          </div>
        )}

        {data.predicted_kg != null && (
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Calibrated:</span>
            <span className="font-medium text-blue-600">
              {data.predicted_kg.toFixed(1)} kg
            </span>
          </div>
        )}

        {data.actual_kg != null && (
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Actual:</span>
            <span className="font-medium text-emerald-600">
              {data.actual_kg.toFixed(1)} kg
            </span>
          </div>
        )}

        {data.upper_bound != null && data.lower_bound != null && (
          <div className="flex justify-between gap-4 pt-1 border-t border-slate-100 mt-1">
            <span className="text-slate-400 text-xs">Band:</span>
            <span className="text-slate-400 text-xs">
              {data.lower_bound.toFixed(1)} - {data.upper_bound.toFixed(1)} kg
            </span>
          </div>
        )}

        {data.error_pct != null && (
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Error:</span>
            <span className={cn(
              'font-medium',
              Math.abs(data.error_pct) < 15 ? 'text-emerald-600' :
              Math.abs(data.error_pct) < 30 ? 'text-amber-600' : 'text-red-600'
            )}>
              {data.error_pct > 0 ? '+' : ''}{data.error_pct.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Chart configuration constants
 */
const CHART_CONFIG = {
  grid: {
    strokeDasharray: '3 3',
    stroke: '#e2e8f0',
    horizontal: true,
    vertical: false
  },
  axis: {
    fontSize: 12,
    fill: '#64748b',
    tickLine: false,
    axisLine: { stroke: '#e2e8f0' }
  },
  colors: {
    rawPrediction: '#9ca3af',     // Grey-400 - original prediction
    calibrated: '#3b82f6',         // Blue-500 - calibrated prediction
    actual: '#10b981',             // Emerald-500 - actuals
    band: '#e0f2fe',               // Sky-100 - confidence band
    bandStroke: '#7dd3fc',         // Sky-300 - band border
    nowLine: '#6366f1'             // Indigo-500 - "now" marker
  },
  margin: {
    top: 10,
    right: 20,
    bottom: 20,
    left: 45
  }
}

/**
 * PredictionChart - Main chart component
 *
 * @param {Object} props
 * @param {Array} props.data - Prediction trend data from getPredictionTrend
 *   Each item: { prediction_week, raw_predicted_kg, predicted_kg, actual_kg, upper_bound, lower_bound }
 * @param {number} [props.height=300] - Chart height in pixels
 * @param {boolean} [props.showCard=true] - Wrap in Card component
 * @param {string} [props.className] - Additional CSS classes
 */
export default function PredictionChart({
  data = [],
  height = 300,
  showCard = true,
  className
}) {
  // Transform data for Recharts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data.map((item) => ({
      ...item,
      // Create a band value pair for Area chart
      confidenceBand: item.upper_bound != null && item.lower_bound != null
        ? [item.lower_bound, item.upper_bound]
        : null
    }))
  }, [data])

  // Find current week for "now" marker
  const nowWeek = useMemo(() => {
    const today = new Date()
    // Find the closest week to today
    const closestWeek = chartData.find(item => {
      if (!item.prediction_week) return false
      const weekDate = new Date(item.prediction_week)
      const diffDays = Math.abs((today - weekDate) / (1000 * 60 * 60 * 24))
      return diffDays < 7
    })
    return closestWeek?.prediction_week || null
  }, [chartData])

  // Calculate Y-axis domain
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 50]

    let maxValue = 0
    let minValue = Infinity

    chartData.forEach(point => {
      const values = [
        point.raw_predicted_kg,
        point.predicted_kg,
        point.actual_kg,
        point.upper_bound
      ].filter(v => v != null)

      if (values.length > 0) {
        maxValue = Math.max(maxValue, ...values)
        minValue = Math.min(minValue, ...values.filter(v => v > 0))
      }

      if (point.lower_bound != null && point.lower_bound > 0) {
        minValue = Math.min(minValue, point.lower_bound)
      }
    })

    // Add padding
    const padding = (maxValue - minValue) * 0.1 || 5
    const domainMin = Math.max(0, Math.floor((minValue - padding) / 5) * 5)
    const domainMax = Math.ceil((maxValue + padding) / 5) * 5

    return [domainMin, Math.max(domainMax, 10)]
  }, [chartData])

  // Handle empty data
  if (!data || data.length === 0) {
    const emptyContent = (
      <div
        className="flex items-center justify-center bg-slate-50 rounded-lg border border-dashed border-slate-200"
        style={{ height }}
      >
        <span className="text-sm text-slate-500">No prediction data available</span>
      </div>
    )

    if (!showCard) return <div className={className}>{emptyContent}</div>

    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">Prediction Accuracy</CardTitle>
          <CardDescription>Historical predictions vs actuals</CardDescription>
        </CardHeader>
        <CardContent>
          {emptyContent}
        </CardContent>
      </Card>
    )
  }

  const chartContent = (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={chartData} margin={CHART_CONFIG.margin}>
        {/* Grid */}
        <CartesianGrid
          strokeDasharray={CHART_CONFIG.grid.strokeDasharray}
          stroke={CHART_CONFIG.grid.stroke}
          horizontal={CHART_CONFIG.grid.horizontal}
          vertical={CHART_CONFIG.grid.vertical}
        />

        {/* X-Axis: Week dates */}
        <XAxis
          dataKey="prediction_week"
          tickFormatter={formatWeekLabel}
          fontSize={CHART_CONFIG.axis.fontSize}
          fill={CHART_CONFIG.axis.fill}
          tickLine={CHART_CONFIG.axis.tickLine}
          axisLine={CHART_CONFIG.axis.axisLine}
        />

        {/* Y-Axis: Kilograms */}
        <YAxis
          domain={yDomain}
          fontSize={CHART_CONFIG.axis.fontSize}
          fill={CHART_CONFIG.axis.fill}
          tickLine={CHART_CONFIG.axis.tickLine}
          axisLine={CHART_CONFIG.axis.axisLine}
          tickFormatter={(value) => `${value}`}
          width={40}
        />

        {/* Tooltip */}
        <Tooltip content={<CustomTooltip />} />

        {/* Confidence Band (shaded area) */}
        <Area
          type="monotone"
          dataKey="upper_bound"
          stroke={CHART_CONFIG.colors.bandStroke}
          strokeWidth={1}
          fill={CHART_CONFIG.colors.band}
          fillOpacity={0.6}
          name="Upper bound"
        />
        <Area
          type="monotone"
          dataKey="lower_bound"
          stroke={CHART_CONFIG.colors.bandStroke}
          strokeWidth={1}
          fill="white"
          fillOpacity={1}
          name="Lower bound"
        />

        {/* Original Prediction (dashed grey) */}
        <Line
          type="monotone"
          dataKey="raw_predicted_kg"
          stroke={CHART_CONFIG.colors.rawPrediction}
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
          activeDot={{ r: 4, fill: CHART_CONFIG.colors.rawPrediction }}
          name="Original"
        />

        {/* Calibrated Prediction (solid blue) */}
        <Line
          type="monotone"
          dataKey="predicted_kg"
          stroke={CHART_CONFIG.colors.calibrated}
          strokeWidth={2}
          dot={{ fill: CHART_CONFIG.colors.calibrated, r: 3 }}
          activeDot={{ r: 5, fill: CHART_CONFIG.colors.calibrated }}
          name="Calibrated"
        />

        {/* Actuals (green) */}
        <Line
          type="monotone"
          dataKey="actual_kg"
          stroke={CHART_CONFIG.colors.actual}
          strokeWidth={2}
          dot={{ fill: CHART_CONFIG.colors.actual, r: 4, strokeWidth: 2, stroke: 'white' }}
          activeDot={{ r: 6, fill: CHART_CONFIG.colors.actual }}
          name="Actual"
          connectNulls={false}
        />

        {/* "Now" marker */}
        {nowWeek && (
          <ReferenceLine
            x={nowWeek}
            stroke={CHART_CONFIG.colors.nowLine}
            strokeWidth={2}
            strokeDasharray="4 4"
            label={{
              value: 'Now',
              position: 'top',
              fill: CHART_CONFIG.colors.nowLine,
              fontSize: 11,
              fontWeight: 500
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )

  if (!showCard) {
    return <div className={className}>{chartContent}</div>
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Prediction Accuracy</CardTitle>
        <CardDescription>
          Historical predictions vs actuals with confidence band
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartContent}

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-slate-400" style={{ borderStyle: 'dashed', borderWidth: '1px 0 0 0', borderColor: '#9ca3af' }} />
            <span className="text-slate-600">Original</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-blue-500 rounded" />
            <span className="text-slate-600">Calibrated</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-slate-600">Actual</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 bg-sky-100 border border-sky-300 rounded" />
            <span className="text-slate-600">Confidence band</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
